import { Controller } from "@hotwired/stimulus";
import Sortable from "sortablejs";

const ITEM_SELECTOR = "[data-sortable-list-target='item']";
const HANDLE_SELECTOR = "[data-sortable-list-target='handle']";
const POSITION_SELECTOR = "[data-sortable-list-target='position']";
const DESTROY_CONTROL_SELECTOR = "[data-sortable-list-destroy-control]";
const DESTROY_BUTTON_SELECTOR = "[data-sortable-list-destroy-button]";
const DESTROY_SELECTOR = "input[type='checkbox'][name$='[_destroy]']";
const FIELD_SELECTOR = "input[type='text'], textarea";

export default class extends Controller {
  connect() {
    this.sortable = null;
    this.initializeSortable();

    this.element.sortable = this.sortable;
    this.refresh();
  }

  initializeSortable() {
    try {
      this.sortable = Sortable.create(this.element, {
        handle: HANDLE_SELECTOR,
        draggable: ITEM_SELECTOR,
        animation: 150,
        ghostClass: "sortable-list--ghost",
        chosenClass: "sortable-list--chosen",
        dragClass: "sortable-list--dragging",
        onEnd: () => {
          this.refresh();
        }
      });
    } catch (error) {
      console.warn("Sortable list failed to initialize.", error);
      this.sortable = null;
    }
  }

  disconnect() {
    if (this.sortable) {
      this.sortable.destroy();
      this.sortable = null;
    }

    this.element.sortable = null;
  }

  refresh() {
    this.updatePositions();
    this.updateFieldLabels();
    this.updateHandleVisibility();
    this.updateDestroyControlVisibility();
  }

  updatePositions() {
    let position = 1;

    this.items().forEach((item) => {
      if (this.isMarkedForDestroy(item)) return;

      const positionInput = this.positionInputForItem(item);
      if (!positionInput) return;

      positionInput.value = position;
      position += 1;
    });
  }

  updateFieldLabels() {
    const fieldLabelPrefix = this.element.dataset.sortableListFieldLabelPrefix;
    const destroyLabelPrefix = this.element.dataset.sortableListDestroyLabelPrefix;

    this.items().forEach((item) => {
      if (this.isMarkedForDestroy(item)) return;

      const positionInput = this.positionInputForItem(item);
      const position = Number.parseInt(positionInput?.value || "", 10);
      if (Number.isNaN(position)) return;

      const field = item.querySelector(FIELD_SELECTOR);
      if (field && fieldLabelPrefix) {
        const labelText = `${fieldLabelPrefix} ${position}`;
        field.setAttribute("aria-label", labelText);
      }

      const destroyButton = this.destroyButtonForItem(item);
      if (destroyButton && destroyLabelPrefix) {
        destroyButton.setAttribute("aria-label", destroyLabelPrefix);
      }
    });
  }

  updateDestroyControlVisibility() {
    const activeItemCount = this.items().filter((item) => !this.isMarkedForDestroy(item)).length;

    this.items().forEach((item) => {
      const isDestroyed = this.isMarkedForDestroy(item);
      item.dataset.sortableListDestroyed = String(isDestroyed);
      item.hidden = isDestroyed;

      const destroyControl = this.destroyControlForItem(item);
      if (!destroyControl) return;

      destroyControl.classList.toggle("hidden", !this.shouldShowDestroyControl(activeItemCount));

      const destroyButton = this.destroyButtonForItem(item);
      if (destroyButton) {
        destroyButton.setAttribute("aria-pressed", String(isDestroyed));
      }
    });
  }

  updateHandleVisibility() {
    const activeItemCount = this.items().filter((item) => !this.isMarkedForDestroy(item)).length;

    this.items().forEach((item) => {
      const handle = this.handleForItem(item);
      if (!handle) return;

      handle.classList.toggle("hidden", activeItemCount <= 1);
    });
  }

  items() {
    return Array.from(this.element.children).filter((child) => child.matches(ITEM_SELECTOR));
  }

  positionInputForItem(item) {
    return item.querySelector(POSITION_SELECTOR);
  }

  destroyButtonForItem(item) {
    return item.querySelector(DESTROY_BUTTON_SELECTOR);
  }

  destroyControlForItem(item) {
    return item.querySelector(DESTROY_CONTROL_SELECTOR);
  }

  handleForItem(item) {
    return item.querySelector(HANDLE_SELECTOR);
  }

  shouldShowDestroyControl(activeItemCount) {
    return this.allowEmpty ? activeItemCount > 0 : activeItemCount > 1;
  }

  get allowEmpty() {
    return this.element.dataset.sortableListAllowEmpty === "true";
  }

  toggleDestroy(event) {
    event.preventDefault();

    const button = event.currentTarget;
    const item = button?.closest(ITEM_SELECTOR);
    const destroyInput = item?.querySelector(DESTROY_SELECTOR);
    if (!destroyInput) return;

    destroyInput.checked = !destroyInput.checked;
    this.refresh();
  }

  isMarkedForDestroy(item) {
    const destroyInput = item.querySelector(DESTROY_SELECTOR);
    return destroyInput?.checked === true;
  }
}
